import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, Button, Table, Row, Col, Alert, Badge } from 'react-bootstrap';
import api from '../../../utils/api';

const TYPES = [
  { value: 'run', label: 'Run' },
  { value: 'walk', label: 'Walk' },
  { value: 'run_walk', label: 'Run/Walk' },
  { value: 'strength', label: 'Strength' },
  { value: 'bike', label: 'Bike' },
  { value: 'golf', label: 'Golf' },
  { value: 'other', label: 'Other' },
];

const typeLabel = (v) => (TYPES.find((t) => t.value === v) || { label: v }).label;
const todayStr = () => new Date().toLocaleDateString('en-CA');

const emptyForm = { date: todayStr(), type: 'walk', durationMin: '', distanceMi: '', avgHR: '', notes: '' };

const WorkoutsTab = () => {
  const [workouts, setWorkouts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/health/workouts?days=180');
      setWorkouts(data);
    } catch (err) {
      setError('Failed to load workouts.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      date: form.date,
      type: form.type,
      durationMin: form.durationMin === '' ? undefined : Number(form.durationMin),
      distanceMi: form.distanceMi === '' ? undefined : Number(form.distanceMi),
      avgHR: form.avgHR === '' ? undefined : Number(form.avgHR),
      notes: form.notes,
    };
    try {
      if (editingId) await api.put(`/health/workouts/${editingId}`, payload);
      else await api.post('/health/workouts', payload);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      setError('Save failed.');
    }
  };

  const edit = (w) => {
    setEditingId(w._id);
    setForm({
      date: w.date,
      type: w.type,
      durationMin: w.durationMin ?? '',
      distanceMi: w.distanceMi ?? '',
      avgHR: w.avgHR ?? '',
      notes: w.notes || '',
    });
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this workout?')) return;
    await api.delete(`/health/workouts/${id}`);
    load();
  };

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      <Card className="mb-3">
        <Card.Header>{editingId ? 'Edit workout' : 'Log a workout'}</Card.Header>
        <Card.Body>
          <Form onSubmit={submit}>
            <Row className="g-2 align-items-end">
              <Col xs={6} md={2}>
                <Form.Label>Date</Form.Label>
                <Form.Control type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label>Type</Form.Label>
                <Form.Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={4} md={2}>
                <Form.Label>Minutes</Form.Label>
                <Form.Control type="number" value={form.durationMin} onChange={(e) => setForm({ ...form, durationMin: e.target.value })} />
              </Col>
              <Col xs={4} md={2}>
                <Form.Label>Miles</Form.Label>
                <Form.Control type="number" step="0.01" value={form.distanceMi} onChange={(e) => setForm({ ...form, distanceMi: e.target.value })} />
              </Col>
              <Col xs={4} md={2}>
                <Form.Label>Avg HR</Form.Label>
                <Form.Control type="number" value={form.avgHR} onChange={(e) => setForm({ ...form, avgHR: e.target.value })} />
              </Col>
              <Col xs={12} md={2}>
                <Button type="submit" className="w-100">{editingId ? 'Update' : 'Add'}</Button>
              </Col>
              <Col xs={12}>
                <Form.Control placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Col>
            </Row>
            {editingId && (
              <Button variant="link" size="sm" className="mt-2 p-0" onClick={() => { setEditingId(null); setForm(emptyForm); }}>
                Cancel edit
              </Button>
            )}
          </Form>
        </Card.Body>
      </Card>

      <Table striped hover responsive size="sm">
        <thead>
          <tr>
            <th>Date</th><th>Type</th><th>Min</th><th>Miles</th><th>Avg HR</th><th>Source</th><th>Notes</th><th></th>
          </tr>
        </thead>
        <tbody>
          {workouts.map((w) => (
            <tr key={w._id}>
              <td>{w.date}</td>
              <td>{typeLabel(w.type)}</td>
              <td>{w.durationMin ?? ''}</td>
              <td>{w.distanceMi ?? ''}</td>
              <td>{w.avgHR ?? ''}</td>
              <td>{w.source === 'oura' ? <Badge bg="info">Oura</Badge> : <Badge bg="secondary">Manual</Badge>}</td>
              <td className="text-muted">{w.notes}</td>
              <td className="text-end">
                {w.source !== 'oura' && (
                  <Button variant="link" size="sm" className="p-0 me-2" onClick={() => edit(w)}>edit</Button>
                )}
                <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => remove(w._id)}>delete</Button>
              </td>
            </tr>
          ))}
          {workouts.length === 0 && (
            <tr><td colSpan={8} className="text-muted">No workouts in the last 180 days.</td></tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default WorkoutsTab;
